import type { Product } from '../insurance-products/index.ts'

/**
 * What happens to the existing contracts of a retired product. New business is closed for every retired product, and
 * their existing contracts are neither endorsed nor renewed.
 */
type ExistingContractsHandling = 'terminate-at-expiry' | 'none'

type RetiredProduct = {
  existingContracts: ExistingContractsHandling
  /**
   * The products that take its place for new business. A duplicate of one of its quotes is allocated to one of them, and
   * the duplication maps its answers onto each. The first one is the product a link built for it now selects.
   */
  successors: readonly Product[]
}

const RETIRED_PRODUCTS = {
  // Wakam stopped carrying these products on 1 October 2026.
  mrpw: { existingContracts: 'terminate-at-expiry', successors: ['fr-mrpaxe', 'mrpa'] },
  'fr-rcpw': { existingContracts: 'terminate-at-expiry', successors: ['fr-rcpaxe', 'rcph'] },
  'es-mrpw': { existingContracts: 'terminate-at-expiry', successors: ['es-mrpaxe'] },
  // Never sold in production, so there is no contract to terminate.
  'fr-rccw': { existingContracts: 'none', successors: [] },
} as const satisfies Partial<Record<Product, RetiredProduct>>

const retiredProductsByProduct: Partial<Record<Product, RetiredProduct>> = RETIRED_PRODUCTS

type RetiredProducts = typeof RETIRED_PRODUCTS

export type RetiredProductWithSuccessors = {
  [P in keyof RetiredProducts]: RetiredProducts[P]['successors'] extends readonly [] ? never : P
}[keyof RetiredProducts]

export type RetiredProductSuccessor<P extends RetiredProductWithSuccessors> = RetiredProducts[P]['successors'][number]

export function isRetiredProductWithSuccessors(product: Product): product is RetiredProductWithSuccessors {
  return (retiredProductsByProduct[product]?.successors.length ?? 0) > 0
}

export function getRetiredProductSuccessors<P extends RetiredProductWithSuccessors>(
  product: P,
): readonly RetiredProductSuccessor<P>[] {
  return RETIRED_PRODUCTS[product].successors
}

export function isRetiredProduct(product: Product): boolean {
  return retiredProductsByProduct[product] !== undefined
}

/** The product a link built for a retired product now selects, or undefined when it is not retired or has no successor. */
export function getRetiredProductSuccessor(product: Product): Product | undefined {
  return retiredProductsByProduct[product]?.successors[0]
}

export function includesRetiredProduct(selectedProducts: readonly Product[]): boolean {
  return selectedProducts.some(isRetiredProduct)
}

const IS_TERMINATED_AT_EXPIRY: Record<ExistingContractsHandling, boolean> = {
  'terminate-at-expiry': true,
  none: false,
}

export function isRetiredProductTerminatedAtExpiry(product: Product): boolean {
  const retiredProduct = retiredProductsByProduct[product]
  return retiredProduct !== undefined && IS_TERMINATED_AT_EXPIRY[retiredProduct.existingContracts]
}
